import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import * as React from 'react';
import { RefObject } from 'react';

import { BANNERS_IN_GROUP_LIMIT } from '../../../constants';
import { Bem } from '../../../service/Bem/Bem';
import { CampaignType, GroupType } from '../../../typings/domain';
import { GdAdGroupPrimaryStatus } from '../../../typings/graphql-grid';
import { CampaignAndGroupSelectorEntityToSelectType } from '../../../typings/state';
import { HoverTooltipWrapper } from '../../HoverTooltipWrapper/HoverTooltipWrapper';
import { Loader } from '../../Loader/Loader';
import { TextInputDecorator } from '../../TextInputDecorator/TextInputDecorator';

import './Selector.css';
import * as keyset from './Selector.i18n';
import { SelectorItem } from './SelectorItem/SelectorItem';

const i18n = i18nFactory(keyset);

export interface SelectorItems {
    id: string;
    name: string;
    type: CampaignType;
    isDisabled?: boolean;
    minusKeywords?: string[];
    regionsIds?: number[];
    adsCount?: number;
    groupStatus?: GdAdGroupPrimaryStatus;
    isAutoBudget?: boolean;
    groupType?: GroupType;
}

export interface SelectorProps {
    entitityType?: CampaignAndGroupSelectorEntityToSelectType;
    placeholder?: string;
    items?: Array<SelectorItems>;
    isLoading: boolean;
    onInputChange: (text: string) => void;
    onItemSelect: (selectorItems: SelectorItems) => void;
    itemSelected?: SelectorItems;
    inputText?: string;
    emptyText?: string;
    awaitText?: string;
    errorText?: string;
    isActive?: boolean;
    cls?: string;
}

const BLOCK = 'selector';

export class Selector extends React.PureComponent<SelectorProps> {
    private preselectRef: RefObject<HTMLDivElement> = React.createRef();

    componentDidUpdate() {
        if (this.preselectRef.current) {
            this.preselectRef.current.scrollIntoView();
        }
    }

    render() {
        const {
            inputText,
            placeholder,
            isActive,
            onInputChange,
            cls
        } = this.props;

        return (
            <div className={classNames(cls, Bem.cls(BLOCK))}>
                <div className={Bem.cls(BLOCK, 'input')}>
                    <TextInputDecorator
                        text={inputText}
                        placeholder={placeholder}
                        theme={'normal'}
                        view={'classic'}
                        size={'n'}
                        cls={Bem.cls(BLOCK, 'input')}
                        onChange={onInputChange}
                        disabled={!isActive}
                    />
                </div>
                <div className={Bem.cls('selector', 'list')}>
                    {this.getListState()}
                </div>
            </div>
        );
    }

    private getListState = () => {
        const {
            isLoading,
            items,
            emptyText,
            isActive,
            errorText,
            awaitText,
            itemSelected,
            onItemSelect
        } = this.props;

        if (isLoading) {
            return <Loader size={'s'} />;
        }

        if (errorText) {
            return (
                <div className={Bem.cls(BLOCK, 'text-info')}>{errorText}</div>
            );
        }

        if (!isActive) {
            return (
                <div className={Bem.cls(BLOCK, 'text-info')}>{awaitText}</div>
            );
        }

        if (!items || !items.length) {
            return (
                <div className={Bem.cls(BLOCK, 'text-info')}>{emptyText}</div>
            );
        } else {
            return items.map((item, index) => {
                return (
                    <div
                        ref={
                            itemSelected && itemSelected.id === item.id
                                ? this.preselectRef
                                : undefined
                        }
                        onClick={() => onItemSelect(item)}
                        className={Bem.cls('selector', 'item-content', {
                            active: Boolean(
                                itemSelected && item.id === itemSelected.id
                            ),
                            disabled: Boolean(item.isDisabled)
                        })}
                        key={index}
                    >
                        {this.renderItem(item)}
                    </div>
                );
            });
        }
    };

    private renderItem(selectorItem: SelectorItems) {
        const { entitityType, onItemSelect, itemSelected } = this.props;

        const isActive = Boolean(
            itemSelected && selectorItem.id === itemSelected.id
        );

        const renderItem = (
            <div onClick={() => onItemSelect(selectorItem)}>
                <SelectorItem
                    isActive={isActive}
                    isDisabled={Boolean(selectorItem.isDisabled)}
                    name={selectorItem.name}
                    id={selectorItem.id}
                />
            </div>
        );

        switch (entitityType) {
            case CampaignAndGroupSelectorEntityToSelectType.BANNER:
                let tooltipText = undefined;

                // если в группе 50 ообъявлений - нельзя добавить ещё
                if (
                    selectorItem.adsCount !== undefined &&
                    selectorItem.adsCount >= BANNERS_IN_GROUP_LIMIT
                ) {
                    tooltipText = i18n('banners-in-group-limit');
                }

                // если группа архивная - нельзя добавить ещё, игнорируем количество объявлений
                if (
                    selectorItem.groupStatus === GdAdGroupPrimaryStatus.ARCHIVED
                ) {
                    tooltipText = i18n('banners-add-archived-group');
                }

                if (tooltipText) {
                    return (
                        <HoverTooltipWrapper
                            tooltipText={tooltipText}
                            to={['right', 'bottom']}
                            isDoccenterTooltip={false}
                            cls={Bem.cls(BLOCK, 'item')}
                        >
                            <SelectorItem
                                isActive={isActive}
                                isDisabled={true}
                                name={selectorItem.name}
                                id={selectorItem.id}
                            />
                        </HoverTooltipWrapper>
                    );
                }

                return renderItem;
            case CampaignAndGroupSelectorEntityToSelectType.GROUP:
            case CampaignAndGroupSelectorEntityToSelectType.PHRASE:
            case CampaignAndGroupSelectorEntityToSelectType.CAMPAIGN:
            default:
                return renderItem;
        }
    }
}
